import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { RefreshCw, Calendar, TrendingUp, Search, Users, ShieldAlert, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BIAS_META = {
  BULLISH:            { label: "🟢 BULLISH",         color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  SLIGHTLY_BULLISH:   { label: "🟢 LEAN BULL",       color: "bg-emerald-500/10 text-emerald-200 border-emerald-500/30" },
  NEUTRAL:            { label: "⚪ NEUTRAL",         color: "bg-slate-500/15 text-slate-300 border-slate-500/40" },
  BEARISH:            { label: "🔴 BEARISH",         color: "bg-red-500/15 text-red-300 border-red-500/40" },
};

const fmtUsd = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(1)}%`);
const fmtPrice = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);

export default function EarningsRadar({ owner = "Helmi" }) {
  const [upcoming, setUpcoming] = useState([]);
  const [pending, setPending] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [customTicker, setCustomTicker] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [marketWeek, setMarketWeek] = useState({ by_day: {}, total: 0, loading: true });
  const debounceRef = React.useRef(null);

  const fetchUpcoming = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/earnings-radar/upcoming/${owner}?days=45`);
      setUpcoming(data?.upcoming || []);
      setPending(data?.pending || 0);
    } catch (e) {
      setUpcoming([]);
      setPending(0);
    } finally {
      setLoading(false);
    }
  }, [owner]);

  const fetchMarketWeek = useCallback(async () => {
    setMarketWeek((m) => ({ ...m, loading: true }));
    try {
      const { data } = await axios.get(`${API}/api/earnings-radar/market-week?days=7&exclude_owner=${owner}`);
      setMarketWeek({ by_day: data?.by_day || {}, total: data?.total || 0, loading: false, error: data?.error });
    } catch (e) {
      setMarketWeek({ by_day: {}, total: 0, loading: false, error: e?.message });
    }
  }, [owner]);

  useEffect(() => {
    fetchUpcoming();
    fetchMarketWeek();
  }, [fetchUpcoming, fetchMarketWeek]);

  // Auto-refresh every 20s while radars are still being warmed in background
  useEffect(() => {
    if (pending <= 0) return;
    const timer = setInterval(fetchUpcoming, 20000);
    return () => clearInterval(timer);
  }, [pending, fetchUpcoming]);

  // Debounced typeahead search — 2+ chars triggers Yahoo autosuggest
  const onInputChange = (v) => {
    setCustomTicker(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!v || v.length < 2) {
      setSuggestions([]);
      setShowSuggest(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/api/search/stocks?q=${encodeURIComponent(v)}`);
        setSuggestions((data?.results || []).slice(0, 8));
        setShowSuggest(true);
      } catch (e) {
        setSuggestions([]);
        setShowSuggest(false);
      }
    }, 250);
  };

  const pickSuggestion = (symbol) => {
    setCustomTicker(symbol);
    setSuggestions([]);
    setShowSuggest(false);
    openDetail(symbol);
  };

  const openDetail = async (ticker, force = false) => {
    setDetailLoading(true);
    setDetail({ ticker, loading: true });
    try {
      const { data } = await axios.get(`${API}/api/earnings-radar/${ticker}${force ? "?force=true" : ""}`);
      setDetail(data);
    } catch (e) {
      setDetail({ ticker, error: e?.response?.data?.detail || "Erreur de chargement" });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCustom = (e) => {
    e.preventDefault();
    const t = customTicker.trim().toUpperCase();
    if (t) openDetail(t);
  };

  return (
    <div className="space-y-5" data-testid="earnings-radar-tab">
      {/* Header + custom search */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-400" />
            Earnings Radar
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Consensus + Whisper + Implied Move + Beat History + Insider — les données que les hedge funds regardent avant chaque earnings.
          </p>
        </div>
        <div className="flex gap-2">
          <form onSubmit={handleCustom} className="flex gap-2 relative">
            <div className="relative">
              <Input
                value={customTicker}
                onChange={(e) => onInputChange(e.target.value)}
                onFocus={() => customTicker.length >= 2 && setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                placeholder="Tape NVDA, AMD, Apple…"
                className="w-56 bg-slate-900/60 border-slate-700 text-white"
                data-testid="earnings-radar-ticker-input"
                autoComplete="off"
              />
              {showSuggest && suggestions.length > 0 && (
                <div className="absolute top-full left-0 mt-1 w-72 max-h-80 overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-50" data-testid="earnings-radar-suggestions">
                  {suggestions.map((s) => (
                    <button
                      key={s.symbol}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s.symbol); }}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-800 text-left border-b border-slate-800 last:border-0"
                      data-testid={`earnings-radar-suggest-${s.symbol}`}
                    >
                      <div>
                        <div className="text-white text-sm font-semibold">{s.symbol}</div>
                        <div className="text-xs text-slate-400 truncate max-w-[180px]">{s.name}</div>
                      </div>
                      <span className="text-[10px] text-slate-500">{s.exchange}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button type="submit" size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="earnings-radar-search-btn">
              <Search className="w-4 h-4" />
            </Button>
          </form>
          <Button size="sm" variant="outline" onClick={fetchUpcoming} disabled={loading} data-testid="earnings-radar-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Market Week — Interesting earnings hors portefeuille */}
      <Card className="bg-slate-900/60 border-slate-800" data-testid="earnings-radar-market-week">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-slate-200">
            <Zap className="w-4 h-4 text-purple-400" />
            Marché — Cette semaine (hors PF) ({marketWeek.total})
            {marketWeek.loading && <span className="text-xs text-slate-500 ml-2">Chargement…</span>}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {marketWeek.error ? (
            <div className="text-red-400 text-sm py-4">Erreur : {marketWeek.error}</div>
          ) : Object.keys(marketWeek.by_day).length === 0 && !marketWeek.loading ? (
            <div className="text-slate-500 text-sm py-4 text-center">Aucune earnings d'intérêt cette semaine.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {Object.entries(marketWeek.by_day).map(([day, rows]) => (
                <div key={day} className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
                  <div className="text-xs font-semibold text-purple-300 mb-2 uppercase tracking-wider">{day}</div>
                  <div className="space-y-1.5">
                    {rows.slice(0, 10).map((r) => (
                      <button
                        key={`${day}-${r.ticker}`}
                        onClick={() => openDetail(r.ticker)}
                        className="w-full text-left flex items-center justify-between text-xs hover:bg-slate-900 rounded px-1.5 py-1 transition"
                        data-testid={`market-week-${r.ticker}`}
                      >
                        <span className="text-white font-medium">{r.ticker}</span>
                        <span className={`text-[10px] uppercase font-semibold ${r.hour === "bmo" ? "text-amber-400" : r.hour === "amc" ? "text-cyan-400" : "text-slate-500"}`}>
                          {r.hour === "bmo" ? "AV" : r.hour === "amc" ? "AP" : "?"}
                        </span>
                      </button>
                    ))}
                    {rows.length > 10 && (
                      <div className="text-[10px] text-slate-500 pt-1">+{rows.length - 10} autres…</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="text-[10px] text-slate-500 mt-3">
            <span className="text-amber-400 font-semibold">AV</span> = Before Market Open · <span className="text-cyan-400 font-semibold">AP</span> = After Market Close · Cliquer un ticker pour le radar complet · Source : Finnhub, filtré sur SCAN_UNIVERSE (~650 tickers curés)
          </div>
        </CardContent>
      </Card>

      {/* Upcoming grid */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-slate-200">
            <Calendar className="w-4 h-4 text-cyan-400" />
            Portfolio Earnings — 45 prochains jours ({upcoming.length})
            {pending > 0 && (
              <span className="text-xs font-normal text-amber-300 ml-2">
                • {pending} radar{pending > 1 ? "s" : ""} en cours de calcul…
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && upcoming.length === 0 ? (
            <div className="text-slate-400 text-sm py-8 text-center">Chargement des radars… (premier lancement ~30s)</div>
          ) : upcoming.length === 0 ? (
            <div className="text-slate-500 text-sm py-8 text-center">Aucune earnings prévue dans la fenêtre.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {upcoming.map((r) => {
                const meta = BIAS_META[r.bias] || BIAS_META.NEUTRAL;
                return (
                  <button
                    key={r.ticker}
                    onClick={() => openDetail(r.ticker)}
                    className="text-left bg-slate-950/60 hover:bg-slate-900 border border-slate-800 hover:border-slate-600 rounded-lg p-3 transition"
                    data-testid={`earnings-radar-card-${r.ticker}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="text-white font-semibold">{r.ticker}</div>
                        <div className="text-xs text-slate-400 truncate max-w-[140px]">{r.name}</div>
                      </div>
                      <Badge className={`${meta.color} text-xs border`}>{meta.label}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-300 mb-2">
                      <span>📅 J{r.days_to_earnings <= 0 ? "-0" : `+${r.days_to_earnings}`}</span>
                      <span>{fmtPrice(r.current_price)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                      <div className="text-slate-400">Beat rate</div>
                      <div className="text-right text-emerald-300">{fmtPct(r.beat_rate)}</div>
                      <div className="text-slate-400">Implied move</div>
                      <div className="text-right text-amber-300">±{fmtPct(r.implied_move_pct)}</div>
                      <div className="text-slate-400">Short float</div>
                      <div className="text-right text-slate-200">{fmtPct(r.short_pct_float)}</div>
                      <div className="text-slate-400">Gamma</div>
                      <div className={`text-right font-semibold ${r.gamma_regime === "POSITIVE_GAMMA" ? "text-emerald-300" : r.gamma_regime === "NEGATIVE_GAMMA" ? "text-red-300" : "text-slate-500"}`}>
                        {r.gamma_regime === "POSITIVE_GAMMA" ? "🟢 POS" : r.gamma_regime === "NEGATIVE_GAMMA" ? "🔴 NEG" : "—"}
                        {r.gamma_flip_level != null && <span className="text-slate-500 ml-1">${r.gamma_flip_level.toFixed(0)}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail modal-like card */}
      {detail && (
        <Card className="bg-gradient-to-br from-slate-900/80 to-slate-950/80 border-amber-500/30" data-testid="earnings-radar-detail">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-amber-400" />
                {detail.ticker} — {detail.name || "Radar"}
              </CardTitle>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => openDetail(detail.ticker, true)} disabled={detailLoading} data-testid="earnings-radar-force-refresh">
                  <RefreshCw className={`w-4 h-4 ${detailLoading ? "animate-spin" : ""}`} />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setDetail(null)} data-testid="earnings-radar-close-detail">✕</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {detail.loading ? (
              <div className="text-slate-400 py-6 text-center">Analyse en cours… (peut prendre ~15s au premier appel)</div>
            ) : detail.error ? (
              <div className="text-red-400 py-6 text-center">{detail.error}</div>
            ) : (
              <DetailBody data={detail} />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

const DetailBody = ({ data }) => {
  const p = data.prediction || {};
  const bias = BIAS_META[p.bias] || BIAS_META.NEUTRAL;
  const bh = data.beat_history || {};
  const im = data.implied_move || {};
  const ia = data.insider_activity || {};

  return (
    <div className="space-y-4">
      {/* Header bias + KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-[10px] uppercase text-slate-500">Cours</div>
          <div className="text-white text-lg font-bold">{fmtPrice(data.current_price)}</div>
        </div>
        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-[10px] uppercase text-slate-500">J avant earnings</div>
          <div className="text-white text-lg font-bold">
            {data.days_to_earnings != null ? `J${data.days_to_earnings <= 0 ? "-0" : `+${data.days_to_earnings}`}` : "—"}
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {data.next_earnings_date ? new Date(data.next_earnings_date).toLocaleDateString("fr-FR") : "TBD"}
          </div>
        </div>
        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-[10px] uppercase text-slate-500">EPS Consensus</div>
          <div className="text-white text-lg font-bold">
            {data.consensus?.eps_estimate != null ? `$${data.consensus.eps_estimate.toFixed(2)}` : "—"}
          </div>
        </div>
        <div className={`rounded-lg p-3 border ${bias.color}`}>
          <div className="text-[10px] uppercase opacity-70">Bias composite</div>
          <div className="text-lg font-bold">{bias.label}</div>
          <div className="text-[10px] opacity-80 mt-0.5">Score {p.score >= 0 ? "+" : ""}{p.score}</div>
        </div>
      </div>

      {/* Signaux */}
      {p.signals?.length > 0 && (
        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Signaux détectés
          </div>
          <ul className="text-sm text-slate-200 space-y-1">
            {p.signals.map((s, i) => (
              <li key={i}>• {s}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Zero Gamma Flip — dealer positioning */}
      {data.gamma_flip && (data.gamma_flip.regime || data.gamma_flip.flip_level != null) && (
        <div className={`rounded-lg p-3 border ${data.gamma_flip.regime === "POSITIVE_GAMMA" ? "bg-emerald-500/5 border-emerald-500/30" : "bg-red-500/5 border-red-500/30"}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase text-slate-400 flex items-center gap-1">
              ⚡ Zero Gamma Flip (dealers)
            </div>
            <Badge className={data.gamma_flip.regime === "POSITIVE_GAMMA" ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/40" : "bg-red-500/20 text-red-200 border-red-500/40"}>
              {data.gamma_flip.regime === "POSITIVE_GAMMA" ? "🟢 POSITIVE" : "🔴 NEGATIVE"}
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm mb-2">
            <div>
              <div className="text-[10px] uppercase text-slate-500">Flip level</div>
              <div className="text-white font-bold">{data.gamma_flip.flip_level != null ? `$${data.gamma_flip.flip_level}` : "hors ±15%"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">Distance</div>
              <div className={`font-bold ${data.gamma_flip.regime === "POSITIVE_GAMMA" ? "text-emerald-300" : "text-red-300"}`}>
                {data.gamma_flip.distance_pct != null ? `${data.gamma_flip.distance_pct > 0 ? "+" : ""}${data.gamma_flip.distance_pct}%` : "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">GEX / 1%</div>
              <div className="text-white font-bold">{data.gamma_flip.current_gex_millions != null ? `$${data.gamma_flip.current_gex_millions}M` : "—"}</div>
            </div>
          </div>
          <p className="text-xs text-slate-300 leading-relaxed">{data.gamma_flip.interpretation}</p>
          <div className="text-[10px] text-slate-500 mt-2 pt-2 border-t border-slate-800">
            Au-dessus du flip = dealers achètent les dips (stabilisant). Sous le flip = dealers vendent la faiblesse (trap door, dips amplifiés).
          </div>
        </div>
      )}

      {/* Beat history + Implied Move + Insider */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-xs uppercase text-slate-500 mb-2">Beat History (8Q)</div>
          <div className="text-2xl font-bold text-emerald-300">{bh.beats}/{bh.total}</div>
          <div className="text-xs text-slate-400">Beat rate: <span className="text-emerald-300">{fmtPct(bh.beat_rate)}</span></div>
          <div className="text-xs text-slate-400">Surprise moy: <span className="text-amber-300">+{bh.avg_surprise_pct}%</span></div>
          <div className="mt-2 space-y-0.5 text-[11px] max-h-32 overflow-y-auto">
            {bh.recent?.map((q, i) => (
              <div key={i} className="flex justify-between border-b border-slate-900 py-0.5">
                <span className="text-slate-500">{q.date}</span>
                <span className={q.beat ? "text-emerald-300" : "text-red-300"}>
                  {q.beat ? "✓" : "✗"} {q.surprise_pct != null ? `${q.surprise_pct > 0 ? "+" : ""}${q.surprise_pct}%` : ""}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-xs uppercase text-slate-500 mb-2">Implied Move (Options)</div>
          {im.implied_move_pct != null ? (
            <>
              <div className="text-2xl font-bold text-amber-300">±{im.implied_move_pct}%</div>
              <div className="text-xs text-slate-400 mt-1">Straddle: <span className="text-white">${im.straddle_price}</span></div>
              <div className="text-xs text-slate-400">Expiry: <span className="text-white">{im.expiry_used}</span></div>
              <div className="text-[10px] text-slate-500 mt-2">
                = mouvement que le marché price déjà. Si &lt; avg historique = options &quot;cheap&quot; (biais directional), sinon straddle.
              </div>
            </>
          ) : (
            <div className="text-slate-500 text-sm">Pas de chaine d&apos;options disponible.</div>
          )}
        </div>

        <div className="bg-slate-950/70 rounded-lg p-3 border border-slate-800">
          <div className="text-xs uppercase text-slate-500 mb-2 flex items-center gap-1">
            <Users className="w-3 h-3" /> Insider Activity (90j)
          </div>
          <div className={`text-2xl font-bold ${ia.net_buy_usd > 0 ? "text-emerald-300" : ia.net_buy_usd < 0 ? "text-red-300" : "text-slate-400"}`}>
            {ia.net_buy_usd > 0 ? "+" : ""}{fmtUsd(ia.net_buy_usd)}
          </div>
          <div className="text-xs text-slate-400 mt-1">Achats: <span className="text-emerald-300">{ia.buys}</span> · Ventes: <span className="text-red-300">{ia.sells}</span></div>
          <div className="text-xs text-slate-400">Insiders actifs: <span className="text-white">{ia.buyer_count}</span></div>
          {data.short_pct_float != null && (
            <div className="text-xs text-slate-400 mt-2 pt-2 border-t border-slate-900">
              Short float: <span className="text-white">{fmtPct(data.short_pct_float)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="text-[10px] text-slate-600 text-center pt-2">
        Sources: yfinance (consensus, earnings history, options chain, insider tx). Cache 6h. Généré: {data.generated_at ? new Date(data.generated_at).toLocaleString("fr-FR") : "—"}
      </div>
    </div>
  );
};
